import { classifyRequestToBookBasket, type RequestToBookItem } from "@obby/lib";

/**
 * Request-to-book: a V2 ticket with `requiresApproval` is booked as a request
 * (the partner confirms it, then sends a payment link) instead of being paid
 * at checkout.
 *
 * The checkout flows build `ticketSelections` from the tickets they render;
 * this stamps each selection with its ticket's flag on the way out to the
 * host's basket, so the basket checkout can tell a request basket from a paid
 * one without refetching tickets. The server re-classifies from its own
 * ticket rows at checkout, so this is a display hint, never trusted for money.
 *
 * A ticket id with no match (a V1 ticket, or a payload from before the API
 * served the field) counts as not a request, matching the server.
 */
export function stampRequiresApproval<
  T extends { ticketSelections?: Array<{ ticketTypeId: string }> },
>(
  itemData: T,
  ticketTypesV2: ReadonlyArray<{
    id: string;
    requiresApproval?: boolean | null;
    chargeType?: string | null;
    depositCollection?: string | null;
  }>
): T {
  if (!itemData.ticketSelections) return itemData;
  const requestTicketIds = new Set(
    ticketTypesV2.filter((t) => t.requiresApproval).map((t) => t.id)
  );
  // A request whose deposit is paid when it is sent (the ticket's
  // "Collect the deposit: when the request is sent" option).
  const depositAtRequestIds = new Set(
    ticketTypesV2
      .filter((t) => t.requiresApproval && t.chargeType === "deposit" && t.depositCollection === "at_request")
      .map((t) => t.id)
  );
  return {
    ...itemData,
    ticketSelections: itemData.ticketSelections.map((selection) => ({
      ...selection,
      requiresApproval: requestTicketIds.has(selection.ticketTypeId),
      ...(depositAtRequestIds.has(selection.ticketTypeId) ? { depositAtRequest: true } : {}),
    })),
  };
}

/**
 * Whether every line of the basket is a request whose deposit is paid when
 * it is sent (stamped `depositAtRequest`): such a basket is paid on the card
 * form like a deposit checkout — never the "Send request" step — and the
 * server's `requestDeposit` answer confirms it. A display hint only.
 */
export function isDepositAtRequestBasket(items: ReadonlyArray<RequestToBookItem>): boolean {
  return (
    classifyRequestToBookBasket(
      items,
      (selection) =>
        !!selection.requiresApproval &&
        (selection as { depositAtRequest?: boolean }).depositAtRequest === true
    ) === "all"
  );
}

/** The note above the card form of a deposit-at-request basket. */
export function describeDepositAtRequest(
  depositPence: number,
  remainingPence: number,
  formatPrice: (pence: number) => string
): string {
  const balance =
    remainingPence > 0
      ? ` The balance of ${formatPrice(remainingPence)} is due once your request is confirmed.`
      : "";
  return `Pay the ${formatPrice(depositPence)} deposit to send your request. If it isn't accepted, the deposit is refunded in full.${balance}`;
}

/** Completion screen body once a deposit-at-request order is paid. */
export function describeDepositRequestSent(
  depositPence: number,
  remainingPence: number,
  formatPrice: (pence: number) => string,
  schoolName?: string | null
): string {
  const balance =
    remainingPence > 0 ? `; ${formatPrice(remainingPence)} balance due once confirmed` : "";
  return `Request sent — ${formatPrice(depositPence)} deposit paid${balance}. ${schoolName || "The business"} will review your request and email you; if it isn't accepted, your deposit is refunded in full.`;
}

/*
 * ---------------------------------------------------------------------------
 * Basket-checkout side: shared by every surface that runs its own
 * createBasketCheckoutSession submit (widget /basket/checkout, the
 * webcomponents basket overlay, youmo's checkout), so they classify, route
 * and word a request the same way.
 * ---------------------------------------------------------------------------
 */

/**
 * A basket is a request basket when every line is a booking whose ticket has
 * `requiresApproval` (stamped by `stampRequiresApproval` above). Such a basket
 * collects no money: the customer sends a request, the partner confirms it
 * and emails a payment link.
 *
 * The classifier is the server's own (@obby/lib `classifyRequestToBookBasket`,
 * re-exported here); called without a predicate it reads the stamped flag.
 * The server re-classifies from its own ticket rows and stays the source of
 * truth; this only decides what the page shows.
 */
export { classifyRequestToBookBasket };
export type { RequestToBookClass } from "@obby/lib";
export type RequestToBookClassifiableItem = RequestToBookItem;

/** The server's request-to-book verdict on a basket, from its last checkout
 *  session response, keyed to the basket it was given. */
export type ServerRequestToBookAnswer = {
  itemsKey: string;
  isRequestToBook: boolean;
};

/**
 * Identity of what the request-to-book classification depends on: the items
 * and their ticket selections. Buyer details, prices and validation state are
 * deliberately left out, so editing the form doesn't discard the server's
 * answer.
 */
export function requestToBookItemsKey(
  items: ReadonlyArray<{
    id: string;
    type?: string;
    ticketSelections?: ReadonlyArray<{
      ticketTypeId: string;
      quantity?: number;
    }> | null;
  }>
): string {
  return JSON.stringify(
    items.map((i) => [
      i.id,
      i.type,
      (i.ticketSelections ?? []).map((ts) => [ts.ticketTypeId, ts.quantity]),
    ])
  );
}

/**
 * Whether the page treats the basket as a request. The page's own
 * classification reads `requiresApproval` stamped when each item was added
 * and persisted with the basket, so it can go stale if the partner flips the
 * ticket while it sits there. Once the server has classified THIS basket, its
 * answer wins in both directions: "paid" brings back the card form (a stale
 * request flag would otherwise hide the Stripe form and mint an order per
 * click), "request" shows the request step.
 */
export function resolveIsRequestToBook(
  derived: boolean,
  serverAnswer: ServerRequestToBookAnswer | null,
  itemsKey: string
): boolean {
  if (serverAnswer && serverAnswer.itemsKey === itemsKey) {
    return serverAnswer.isRequestToBook;
  }
  return derived;
}

/** Label for a request basket's total: the figure is the deposit due on
 *  confirmation when there is one, otherwise the booking's price. */
export function requestToBookTotalLabel(hasDeposit: boolean): string {
  return hasDeposit ? "Deposit once confirmed" : "Price";
}

type IncomingItems =
  | RequestToBookClassifiableItem
  | ReadonlyArray<RequestToBookClassifiableItem>;

const toItemList = (incoming: IncomingItems): ReadonlyArray<RequestToBookClassifiableItem> =>
  Array.isArray(incoming) ? incoming : [incoming as RequestToBookClassifiableItem];

/**
 * True when adding `incoming` (one item, or several added together — a
 * multi-date pass) would leave the basket mixed (request items next to items
 * paid now), which the server refuses at checkout. That includes a booking
 * that mixes on its own, even into an empty basket: the server refuses it
 * too (REQUEST_TO_BOOK_MIXED_BOOKING), so it's stopped here, at the choice,
 * rather than at the last step.
 */
export function addWouldMixRequestToBook(
  basketItems: ReadonlyArray<RequestToBookClassifiableItem>,
  incoming: IncomingItems
): boolean {
  return classifyRequestToBookBasket([...basketItems, ...toItemList(incoming)]) === "mixed";
}

/**
 * Which side of a refused add holds the request-to-book ticket:
 * - "incoming-request": the customer is booking a request ticket and the
 *   basket holds items paid now — remove those to continue.
 * - "basket-request": the basket holds a request ticket and the customer is
 *   adding something paid now — remove the request ticket to continue.
 * - "incoming-mixed": what's being added mixes both kinds by itself, so
 *   nothing in the basket can fix it.
 */
export type RequestToBookMixKind =
  | "incoming-request"
  | "basket-request"
  | "incoming-mixed"
  | "incoming-deposit-timing";

export type RequestToBookMixNotice = {
  kind: RequestToBookMixKind;
  /** The basket items standing in the way (0 for the "incoming-…" kinds a
   *  booking brings on its own). */
  blockingCount: number;
  /** Whether removing basket items resolves it, i.e. whether to offer the basket. */
  resolvableInBasket: boolean;
  title: string;
  message: string;
};

/**
 * Whether one booking's request-to-book tickets collect their deposits at
 * different times: some when the request is sent (stamped
 * `depositAtRequest`), some once it's confirmed. One checkout can't both
 * charge a deposit and collect nothing, so the server refuses such a booking
 * even on its own (REQUEST_TO_BOOK_MIXED_BOOKING) — those tickets are booked
 * separately. A display hint, as the stamps are; the server judges from its
 * ticket rows by the same rule.
 */
export function bookingMixesDepositTiming(item: RequestToBookClassifiableItem): boolean {
  return (
    classifyRequestToBookBasket([item]) === "all" &&
    classifyRequestToBookBasket(
      [item],
      (selection) => (selection as { depositAtRequest?: boolean }).depositAtRequest === true
    ) === "mixed"
  );
}

/**
 * The notice for an add the server would refuse at checkout — one that
 * `addWouldMixRequestToBook` refuses, or a booking whose request tickets take
 * their deposits at different times — worded for the side that holds the
 * request ticket; null when the add can go in. Shared by every surface that
 * refuses the add, so they all say the same thing.
 */
export function describeRequestToBookMix(
  basketItems: ReadonlyArray<RequestToBookClassifiableItem>,
  incoming: IncomingItems
): RequestToBookMixNotice | null {
  // Refused whatever the basket holds: nothing in it can fix that.
  if (toItemList(incoming).some(bookingMixesDepositTiming)) {
    return {
      kind: "incoming-deposit-timing",
      blockingCount: 0,
      resolvableInBasket: false,
      title: "These tickets need separate bookings",
      message:
        "Some of these request-to-book tickets take their deposit when you send the request and others once it's confirmed, so they can't be booked together. Please book them separately.",
    };
  }
  if (!addWouldMixRequestToBook(basketItems, incoming)) return null;
  const incomingClass = classifyRequestToBookBasket(toItemList(incoming));

  if (incomingClass === "mixed") {
    return {
      kind: "incoming-mixed",
      blockingCount: 0,
      resolvableInBasket: false,
      title: "Request-to-book tickets are booked on their own",
      message:
        "They can't be checked out together with other tickets or items in the same booking. Please book the request-to-book ticket separately.",
    };
  }

  if (incomingClass === "all") {
    // Everything in the basket that isn't purely a request blocks it.
    const blockingCount = basketItems.filter(
      (item) => classifyRequestToBookBasket([item]) !== "all"
    ).length;
    const one = blockingCount === 1;
    return {
      kind: "incoming-request",
      blockingCount,
      resolvableInBasket: true,
      title: "This booking needs its own checkout",
      message: `Request-to-book tickets are checked out on their own, and it looks like you already have ${
        one ? "another item" : `${blockingCount} other items`
      } in your basket. To continue with this booking, please remove ${
        one ? "that item" : "those items"
      } from your basket first. Thank you for your understanding.`,
    };
  }

  // Paid-now items coming in: every basket item carrying a request blocks it.
  const blockingCount = basketItems.filter(
    (item) => classifyRequestToBookBasket([item]) !== "none"
  ).length;
  const one = blockingCount === 1;
  return {
    kind: "basket-request",
    blockingCount,
    resolvableInBasket: true,
    title: "Your basket has a request-to-book ticket",
    message: `Request-to-book tickets are checked out on their own, so this can't be added alongside the ${
      one ? "one" : "ones"
    } already in your basket. To continue, please remove the request-to-book ${
      one ? "ticket" : "tickets"
    } from your basket first — you can book ${one ? "it" : "them"} separately afterwards. Thank you for your understanding.`,
  };
}

/**
 * True when a createBasketCheckoutSession response is a request that collects
 * NOTHING now (v1: the partner confirms, then sends a payment link) — the
 * page shows the request step instead of a card form. A request whose
 * deposit is collected when it is sent is paid on the card form like any
 * deposit checkout, so it is not one of these. This is the per-basket "hide
 * the card form" answer the checkout pages store.
 *
 * The server answers a deposit-at-request session with `requestDeposit: true`
 * and WITHOUT `isRequestToBook` (backward compatibility: cached bundles that
 * predate deposit-at-request read `isRequestToBook` as a free request and
 * would call confirmFreeBasketOrder, which refuses a paid request). Both
 * flags together are still read as the card path, defensively.
 */
export function isUnpaidRequestResponse(data: {
  isRequestToBook?: boolean;
  requestDeposit?: boolean;
}): boolean {
  return data.isRequestToBook === true && data.requestDeposit !== true;
}

/**
 * True when a createBasketCheckoutSession response is a deposit-at-request
 * session: a request whose deposit is paid now on the card form (the page
 * shows the "Pay the deposit to send your request" copy and completes on the
 * "Request sent — deposit paid" variant).
 */
export function isDepositRequestResponse(data: { requestDeposit?: boolean }): boolean {
  return data.requestDeposit === true;
}

/** What the checkout does with a createBasketCheckoutSession response. */
export type CheckoutSessionOutcome =
  | { kind: "confirm"; completionVariant: "paid" | "requested" }
  | { kind: "hosted"; url: string }
  | { kind: "embedded" };

/**
 * A request order (`isRequestToBook`) is fulfilled exactly like a free one —
 * POST /app/confirmFreeBasketOrder, no Stripe — but completes as "requested".
 * The server's flag wins over the page's own classification, so a basket the
 * page didn't recognise (e.g. items added before tickets carried the flag)
 * still never opens a card form for a request.
 *
 * A request whose deposit is collected when it is sent (`requestDeposit`)
 * is the exception: the deposit is paid on the card form (embedded / hosted)
 * and the paid webhook stores the booking as requested.
 */
export function resolveCheckoutSessionOutcome(data: {
  amount?: number;
  isRequestToBook?: boolean;
  requestDeposit?: boolean;
  usedStripeHostedPage?: boolean;
  checkoutUrl?: string | null;
}): CheckoutSessionOutcome {
  if (isUnpaidRequestResponse(data)) {
    return { kind: "confirm", completionVariant: "requested" };
  }
  if (data.amount === 0) return { kind: "confirm", completionVariant: "paid" };
  if (data.usedStripeHostedPage && data.checkoutUrl) {
    return { kind: "hosted", url: data.checkoutUrl };
  }
  return { kind: "embedded" };
}

/**
 * The deposit line under the "Send request" button, or null when the request
 * has no deposit. `depositPence` is the deposit due once confirmed (the
 * basket's charge-now figure), `remainingPence` what's owed after it.
 */
export function describeRequestDeposit(
  depositPence: number,
  remainingPence: number,
  formatPrice: (pence: number) => string
): string | null {
  if (remainingPence <= 0) return null;
  return `You'll pay the ${formatPrice(depositPence)} deposit once your request is confirmed; the balance of ${formatPrice(remainingPence)} is due later.`;
}

/** Completion screen title once a request is sent. */
export const REQUEST_SENT_TITLE = "Request sent!";

/** Completion screen body once a request is sent. */
export function describeRequestSent(schoolName?: string | null): string {
  return `${schoolName || "The business"} will review your request and email you a payment link once it's confirmed. Nothing has been charged.`;
}

/** The line under the "Send request" button. */
export function describeRequestNoPayment(schoolName?: string | null): string {
  return `No payment is taken now. ${schoolName || "The business"} will review your request and send you a payment link.`;
}
